const API_BASE = '/api';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public fields?: Record<string, string>
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new ApiError(
      response.status,
      data.code || 'UNKNOWN_ERROR',
      data.error || 'An error occurred',
      data.fields
    );
  }
  return response.json();
}

function getHeaders(entityId?: string): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (entityId) {
    headers['X-Entity-Id'] = entityId;
  }
  return headers;
}

export const api = {
  // Auth
  auth: {
    login: (username: string, password: string) =>
      fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: getHeaders(),
        credentials: 'include',
        body: JSON.stringify({ username, password }),
      }).then(handleResponse<{ user: User }>),

    logout: () =>
      fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      }).then(handleResponse<{ success: boolean }>),

    me: () =>
      fetch(`${API_BASE}/auth/me`, {
        credentials: 'include',
      }).then(handleResponse<{ user: User; entities: Entity[] }>),

    entities: () =>
      fetch(`${API_BASE}/auth/entities`, {
        credentials: 'include',
      }).then(handleResponse<{ entities: Entity[] }>),
  },

  // Transactions
  transactions: {
    list: (entityId: string, params?: TransactionFilters) => {
      const searchParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined) {
            if (Array.isArray(value)) {
              searchParams.set(key, value.join(','));
            } else {
              searchParams.set(key, String(value));
            }
          }
        });
      }
      return fetch(`${API_BASE}/transactions?${searchParams}`, {
        headers: getHeaders(entityId),
        credentials: 'include',
      }).then(handleResponse<{ data: Transaction[]; meta: PaginationMeta }>);
    },

    export: (entityId: string, params?: Omit<TransactionFilters, 'page' | 'limit'>) => {
      const searchParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined) {
            if (Array.isArray(value)) {
              searchParams.set(key, value.join(','));
            } else {
              searchParams.set(key, String(value));
            }
          }
        });
      }
      return fetch(`${API_BASE}/transactions/export?${searchParams}`, {
        headers: getHeaders(entityId),
        credentials: 'include',
      }).then(handleResponse<{ data: Transaction[] }>);
    },

    get: (entityId: string, id: string) =>
      fetch(`${API_BASE}/transactions/${id}`, {
        headers: getHeaders(entityId),
        credentials: 'include',
      }).then(handleResponse<{ data: Transaction }>),

    create: (entityId: string, data: CreateTransactionInput) =>
      fetch(`${API_BASE}/transactions`, {
        method: 'POST',
        headers: getHeaders(entityId),
        credentials: 'include',
        body: JSON.stringify(data),
      }).then(handleResponse<{ data: Transaction }>),

    update: (entityId: string, id: string, data: UpdateTransactionInput) =>
      fetch(`${API_BASE}/transactions/${id}`, {
        method: 'PATCH',
        headers: getHeaders(entityId),
        credentials: 'include',
        body: JSON.stringify(data),
      }).then(handleResponse<{ data: Transaction }>),

    delete: (entityId: string, id: string) =>
      fetch(`${API_BASE}/transactions/${id}`, {
        method: 'DELETE',
        headers: getHeaders(entityId),
        credentials: 'include',
      }).then(handleResponse<{ success: boolean }>),
  },

  // Movements ledger
  movements: {
    list: (entityId: string, params?: { date?: string; startDate?: string; endDate?: string; myOnly?: boolean; transactionId?: string; walletId?: string }) => {
      const searchParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== '') searchParams.set(key, String(value));
        });
      }
      return fetch(`${API_BASE}/movements?${searchParams}`, {
        headers: getHeaders(entityId),
        credentials: 'include',
      }).then(handleResponse<{ data: WalletMovement[] }>);
    },
    update: (entityId: string, movementId: string, body: { amount?: number; createdAt?: string; transactionId?: string | null; recalculateTransaction?: boolean; walletId?: string; currency?: string; isDebtPayment?: boolean }) =>
      fetch(`${API_BASE}/movements/${movementId}`, {
        method: 'PATCH',
        headers: getHeaders(entityId),
        credentials: 'include',
        body: JSON.stringify(body),
      }).then(handleResponse<{ success: boolean; newAmount: number }>),
    delete: (entityId: string, movementId: string) =>
      fetch(`${API_BASE}/movements/${movementId}`, {
        method: 'DELETE',
        headers: getHeaders(entityId),
        credentials: 'include',
      }).then(handleResponse<{ success: boolean }>),
    create: (entityId: string, body: { walletId: string; amount: number; currency: string; direction: 'in' | 'out'; transactionId?: string | null }) =>
      fetch(`${API_BASE}/movements`, {
        method: 'POST',
        headers: getHeaders(entityId),
        credentials: 'include',
        body: JSON.stringify(body),
      }).then(handleResponse<{ success: boolean; id: string }>),
  },


  // Debt
  debt: {
    list: (entityId: string, params?: DebtFilters) => {
      const searchParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined) searchParams.set(key, String(value));
        });
      }
      return fetch(`${API_BASE}/debt?${searchParams}`, {
        headers: getHeaders(entityId),
        credentials: 'include',
      }).then(handleResponse<{ data: DebtItem[]; meta: PaginationMeta; totals: CurrencyTotal[] }>);
    },

    get: (entityId: string, transactionId: string) =>
      fetch(`${API_BASE}/debt/${transactionId}`, {
        headers: getHeaders(entityId),
        credentials: 'include',
      }).then(handleResponse<DebtDetail>),

    addPayment: (entityId: string, data: CreateDebtPayment) =>
      fetch(`${API_BASE}/debt/payments`, {
        method: 'POST',
        headers: getHeaders(entityId),
        credentials: 'include',
        body: JSON.stringify(data),
      }).then(handleResponse<{ payment: any; remainingIn: string; remainingOut: string; transactionClosed: boolean }>),

    close: (entityId: string, transactionId: string) =>
      fetch(`${API_BASE}/debt/${transactionId}/close`, {
        method: 'POST',
        headers: getHeaders(entityId),
        credentials: 'include',
      }).then(handleResponse<{ success: boolean }>),
  },

  // Reports
  reports: {
    cashflow: (entityId: string, params?: ReportFilters) => {
      const searchParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined) searchParams.set(key, String(value));
        });
      }
      return fetch(`${API_BASE}/reports/cashflow?${searchParams}`, {
        headers: getHeaders(entityId),
        credentials: 'include',
      }).then(handleResponse<CashflowReport>);
    },

    cashflowDetailed: (entityId: string, params?: ReportFilters & { page?: number; limit?: number }) => {
      const searchParams = new URLSearchParams();
      searchParams.set('detailed', 'true');
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined) searchParams.set(key, String(value));
        });
      }
      return fetch(`${API_BASE}/reports/cashflow?${searchParams}`, {
        headers: getHeaders(entityId),
        credentials: 'include',
      }).then(handleResponse<CashflowDetailedReport>);
    },

    balances: (entityId: string) =>
      fetch(`${API_BASE}/reports/balances`, {
        headers: getHeaders(entityId),
        credentials: 'include',
      }).then(handleResponse<BalancesReport>),

    dailySummary: (entityId: string, params?: ReportFilters) => {
      const searchParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined) searchParams.set(key, String(value));
        });
      }
      return fetch(`${API_BASE}/reports/daily-summary?${searchParams}`, {
        headers: getHeaders(entityId),
        credentials: 'include',
      }).then(handleResponse<DailySummaryReport>);
    },

    profit: (entityId: string, params?: ReportFilters) => {
      const searchParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined) searchParams.set(key, String(value));
        });
      }
      return fetch(`${API_BASE}/reports/profit?${searchParams}`, {
        headers: getHeaders(entityId),
        credentials: 'include',
      }).then(handleResponse<ProfitReport>);
    },

    dailyActivity: (entityId: string, date?: string, dateTo?: string, cashDeskId?: string) => {
      const searchParams = new URLSearchParams();
      if (date) searchParams.set('date', date);
      if (dateTo) searchParams.set('dateTo', dateTo);
      if (cashDeskId) searchParams.set('cashDeskId', cashDeskId);
      return fetch(`${API_BASE}/reports/daily-activity?${searchParams}`, {
        headers: getHeaders(entityId),
        credentials: 'include',
      }).then(handleResponse<DailyActivityReport>);
    },
  },

  // Rates
  rates: {
    /** USD-denominated fiat rates (from backend cache or external API). */
    fiatUsd: (entityId: string) =>
      fetch(`${API_BASE}/rates/fiat-usd`, {
        headers: getHeaders(entityId),
        credentials: 'include',
      }).then(handleResponse<{ date: string; usd: Record<string, number> }>),

    reference: (entityId: string, pairs?: string[]) => {
      const searchParams = new URLSearchParams();
      if (pairs && pairs.length > 0) {
        searchParams.set('pairs', pairs.join(','));
      }
      return fetch(`${API_BASE}/rates/reference?${searchParams}`, {
        headers: getHeaders(entityId),
        credentials: 'include',
      }).then(handleResponse<RatesResponse>);
    },

    getRate: (entityId: string, pair: string) =>
      fetch(`${API_BASE}/rates/reference/${pair.replace('/', '-')}`, {
        headers: getHeaders(entityId),
        credentials: 'include',
      }).then(handleResponse<RateItem>),
  },

  // Clients
  clients: {
    list: (entityId: string, params?: ClientFilters) => {
      const searchParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined) searchParams.set(key, String(value));
        });
      }
      return fetch(`${API_BASE}/clients?${searchParams}`, {
        headers: getHeaders(entityId),
        credentials: 'include',
      }).then(handleResponse<{ data: Client[]; meta: PaginationMeta }>);
    },

    get: (entityId: string, id: string) =>
      fetch(`${API_BASE}/clients/${id}`, {
        headers: getHeaders(entityId),
        credentials: 'include',
      }).then(handleResponse<{ data: Client }>),

    search: (entityId: string, query: string) =>
      fetch(`${API_BASE}/clients/search?q=${encodeURIComponent(query)}`, {
        headers: getHeaders(entityId),
        credentials: 'include',
      }).then(handleResponse<{ data: Client[] }>),

    create: (entityId: string, data: CreateClientInput) =>
      fetch(`${API_BASE}/clients`, {
        method: 'POST',
        headers: getHeaders(entityId),
        credentials: 'include',
        body: JSON.stringify(data),
      }).then(handleResponse<{ data: Client }>),

    update: (entityId: string, id: string, data: UpdateClientInput) =>
      fetch(`${API_BASE}/clients/${id}`, {
        method: 'PATCH',
        headers: getHeaders(entityId),
        credentials: 'include',
        body: JSON.stringify(data),
      }).then(handleResponse<{ data: Client }>),

    delete: (entityId: string, id: string) =>
      fetch(`${API_BASE}/clients/${id}`, {
        method: 'DELETE',
        headers: getHeaders(entityId),
        credentials: 'include',
      }).then(handleResponse<{ success: boolean }>),
  },

  // Admin
  admin: {
    users: {
      list: () =>
        fetch(`${API_BASE}/admin/users`, {
          credentials: 'include',
        }).then(handleResponse<{ data: User[] }>),

      create: (data: CreateUserInput) =>
        fetch(`${API_BASE}/admin/users`, {
          method: 'POST',
          headers: getHeaders(),
          credentials: 'include',
          body: JSON.stringify(data),
        }).then(handleResponse<{ data: User }>),

      update: (id: string, data: UpdateUserInput) =>
        fetch(`${API_BASE}/admin/users/${id}`, {
          method: 'PATCH',
          headers: getHeaders(),
          credentials: 'include',
          body: JSON.stringify(data),
        }).then(handleResponse<{ data: User }>),

      setPassword: (id: string, password: string) =>
        fetch(`${API_BASE}/admin/users/${id}/password`, {
          method: 'POST',
          headers: getHeaders(),
          credentials: 'include',
          body: JSON.stringify({ password }),
        }).then(handleResponse<{ success: boolean }>),
    },

    entities: {
      list: () =>
        fetch(`${API_BASE}/admin/entities`, {
          credentials: 'include',
        }).then(handleResponse<{ data: Entity[] }>),

      create: (data: CreateEntityInput) =>
        fetch(`${API_BASE}/admin/entities`, {
          method: 'POST',
          headers: getHeaders(),
          credentials: 'include',
          body: JSON.stringify(data),
        }).then(handleResponse<{ data: Entity }>),
    },

    wallets: {
      list: (entityId: string, params?: { search?: string; type?: string }) => {
        const searchParams = new URLSearchParams();
        if (params?.search !== undefined && params.search !== '') {
          searchParams.set('search', params.search);
        }
        if (params?.type !== undefined && params.type !== '') {
          searchParams.set('type', params.type);
        }
        const query = searchParams.toString();
        return fetch(`${API_BASE}/admin/wallets${query ? `?${query}` : ''}`, {
          headers: getHeaders(entityId),
          credentials: 'include',
        }).then(handleResponse<{ data: Wallet[] }>);
      },

      create: (entityId: string, data: CreateWalletInput) =>
        fetch(`${API_BASE}/admin/wallets`, {
          method: 'POST',
          headers: getHeaders(entityId),
          credentials: 'include',
          body: JSON.stringify(data),
        }).then(handleResponse<{ data: Wallet }>),

      update: (entityId: string, walletId: string, data: Partial<CreateWalletInput> & { isActive?: boolean }) =>
        fetch(`${API_BASE}/admin/wallets/${walletId}`, {
          method: 'PATCH',
          headers: getHeaders(entityId),
          credentials: 'include',
          body: JSON.stringify(data),
        }).then(handleResponse<{ data: Wallet }>),

      delete: (entityId: string, walletId: string) =>
        fetch(`${API_BASE}/admin/wallets/${walletId}`, {
          method: 'DELETE',
          headers: getHeaders(entityId),
          credentials: 'include',
        }).then(handleResponse<{ success: boolean }>),

      setInitialBalance: (entityId: string, walletId: string, data: { amount: string; date: string; currency: string }) =>
        fetch(`${API_BASE}/admin/wallets/${walletId}/initial-balance`, {
          method: 'POST',
          headers: getHeaders(entityId),
          credentials: 'include',
          body: JSON.stringify(data),
        }).then(handleResponse<{ success: boolean }>),

      verifyOnchain: (entityId: string) =>
        fetch(`${API_BASE}/admin/wallets/verify-onchain`, {
          headers: getHeaders(entityId),
          credentials: 'include',
        }).then(handleResponse<{ data: OnchainVerificationResult[] }>),

      onchainTransactions: (entityId: string, walletId: string, from: string, to: string) =>
        fetch(`${API_BASE}/admin/wallets/${walletId}/onchain-transactions?from=${from}&to=${to}`, {
          headers: getHeaders(entityId),
          credentials: 'include',
        }).then(handleResponse<{ data: ReconciliationResult }>),
    },

    currencies: {
      list: () =>
        fetch(`${API_BASE}/admin/currencies`, {
          credentials: 'include',
        }).then(handleResponse<{ data: Currency[] }>),
    },

    categories: {
      list: () =>
        fetch(`${API_BASE}/admin/categories`, {
          credentials: 'include',
        }).then(handleResponse<{ data: ExpenseCategory[] }>),
    },

    clients: {
      list: (params?: ClientFilters) => {
        const searchParams = new URLSearchParams();
        if (params) {
          Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined) searchParams.set(key, String(value));
          });
        }
        return fetch(`${API_BASE}/admin/clients?${searchParams}`, {
          credentials: 'include',
        }).then(handleResponse<{ data: Client[]; meta: PaginationMeta }>);
      },
    },

    periods: {
      list: (entityId: string) =>
        fetch(`${API_BASE}/admin/periods`, {
          headers: getHeaders(entityId),
          credentials: 'include',
        }).then(handleResponse<{ data: AccountingPeriod[] }>),

      create: (entityId: string, data: CreatePeriodInput) =>
        fetch(`${API_BASE}/admin/periods`, {
          method: 'POST',
          headers: getHeaders(entityId),
          credentials: 'include',
          body: JSON.stringify(data),
        }).then(handleResponse<{ data: AccountingPeriod }>),

      close: (entityId: string, periodId: string) =>
        fetch(`${API_BASE}/admin/periods/close`, {
          method: 'POST',
          headers: getHeaders(entityId),
          credentials: 'include',
          body: JSON.stringify({ periodId }),
        }).then(handleResponse<{ data: AccountingPeriod }>),
    },

    logs: {
      list: (params?: LogFilters) => {
        const searchParams = new URLSearchParams();
        if (params) {
          Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined) searchParams.set(key, String(value));
          });
        }
        return fetch(`${API_BASE}/logs?${searchParams}`, {
          credentials: 'include',
        }).then(handleResponse<{ data: LogEntry[]; meta: any }>);
      },
    },
  },

  cashDesks: {
    list: (entityId: string) =>
      fetch(`${API_BASE}/cash-desks`, {
        credentials: 'include',
        headers: { 'X-Entity-Id': entityId },
      }).then(handleResponse<{ data: CashDesk[] }>),

    create: (entityId: string, input: { name: string; description?: string }) =>
      fetch(`${API_BASE}/cash-desks`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Entity-Id': entityId },
        body: JSON.stringify(input),
      }).then(handleResponse<{ data: CashDesk }>),

    update: (entityId: string, cashDeskId: string, input: { name?: string; description?: string | null; isActive?: boolean }) =>
      fetch(`${API_BASE}/cash-desks/${cashDeskId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Entity-Id': entityId },
        body: JSON.stringify(input),
      }).then(handleResponse<{ data: CashDesk }>),

    delete: (entityId: string, cashDeskId: string) =>
      fetch(`${API_BASE}/cash-desks/${cashDeskId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'X-Entity-Id': entityId },
      }).then(handleResponse<{ success: boolean }>),

    assignWallet: (entityId: string, cashDeskId: string, walletId: string) =>
      fetch(`${API_BASE}/cash-desks/${cashDeskId}/wallets/${walletId}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-Entity-Id': entityId },
      }).then(handleResponse<{ success: boolean }>),

    unassignWallet: (entityId: string, cashDeskId: string, walletId: string) =>
      fetch(`${API_BASE}/cash-desks/${cashDeskId}/wallets/${walletId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'X-Entity-Id': entityId },
      }).then(handleResponse<{ success: boolean }>),

    assignUser: (entityId: string, userId: string, cashDeskId: string | null) =>
      fetch(`${API_BASE}/cash-desks/users/${userId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Entity-Id': entityId },
        body: JSON.stringify({ userId, cashDeskId }),
      }).then(handleResponse<{ success: boolean }>),
  },
};

// Types
export interface User {
  id: string;
  username: string;
  fullName: string;
  email: string | null;
  role: 'admin' | 'accountant' | 'cashier' | 'viewer';
  isActive: boolean;
  createdAt: string;
  entities?: { id: string; code: string }[];
}

export interface Entity {
  id: string;
  code: string;
  name: string;
  country?: string;
  defaultCurrency?: string;
  isActive?: boolean;
}

export interface Wallet {
  id: string;
  name: string;
  type: 'cash' | 'bank' | 'crypto' | 'payment_system';
  currencyCode: string;
  address?: string;
  description?: string;
  isActive: boolean;
}

export interface OnchainVerificationResult {
  walletId: string;
  name: string;
  address: string;
  currencyCode: string;
  network: 'tron' | 'ethereum' | 'unknown';
  systemBalance: string;
  onchainBalance: string | null;
  diff: string | null;
  error: string | null;
}

export interface OnchainTransfer {
  txHash: string;
  timestamp: string;
  direction: 'in' | 'out';
  amount: string;
  counterparty: string;
}

export interface SystemMovement {
  id: string;
  transactionId: string | null;
  debtPaymentId: string | null;
  amount: string;
  createdAt: string;
  txHash: string | null;
}

export interface MatchedPair {
  onchain: OnchainTransfer;
  system: SystemMovement;
  timeDiffMinutes: number;
  matchedBy: 'hash' | 'heuristic';
}

export interface ReconciliationResult {
  walletId: string;
  walletName: string;
  address: string;
  network: 'tron' | 'ethereum' | 'unknown';
  currencyCode: string;
  matched: MatchedPair[];
  onchainOnly: OnchainTransfer[];
  systemOnly: SystemMovement[];
}

export interface Currency {
  id: string;
  code: string;
  name: string;
  symbol?: string;
  type: 'fiat' | 'crypto';
  decimals: number;
  isActive: boolean;
}

export interface ExpenseCategory {
  id: string;
  name: string;
  groupType: string;
  isActive: boolean;
}

export interface Transaction {
  id: string;
  type: 'exchange' | 'expense' | 'deposit' | 'withdrawal' | 'transfer';
  status: 'open' | 'closed';
  amountIn?: string;
  currencyIn?: string;
  walletInId?: string;
  walletInName?: string;
  amountOut?: string;
  currencyOut?: string;
  walletOutId?: string;
  walletOutName?: string;
  exchangeRate?: string;
  oracleRate?: string;
  amount?: string;
  currency?: string;
  walletId?: string;
  walletName?: string;
  category?: string;
  expectedAmount?: string;
  debtAmount?: string;
  debtAmountIn?: string;
  debtAmountOut?: string;
  originalDebtAmountIn?: string | null;
  originalDebtAmountOut?: string | null;
  expectedPaymentDate?: string;
  expectedPaymentDateIn?: string;
  expectedPaymentDateOut?: string;
  profitAmount?: string;
  profitCurrency?: string;
  clientId?: string;
  clientName?: string;
  comment?: string;
  version: number;
  createdAt: string;
  updatedAt?: string;
  transactionDate?: string;
  employeeId: string;
  employeeName: string;
  legalEntityId: string;
  txHashIn?: string | null;
  txHashOut?: string | null;
  txHash?: string | null;
  inClosedPeriod?: boolean;
}

export interface TransactionFilters {
  type?: string;
  types?: string[];
  status?: string;
  employeeId?: string;
  clientId?: string;
  clientSearch?: string;
  currencies?: string[];
  startDate?: string;
  endDate?: string;
  date?: 'today';
  // Separate date-IN (created_at) and date-OUT (transaction_date) filters
  startDateIn?: string;  // UTC ISO timestamp — local midnight converted to UTC
  endDateIn?: string;    // UTC ISO timestamp — local 23:59:59 converted to UTC
  startDateOut?: string; // YYYY-MM-DD date string
  endDateOut?: string;   // YYYY-MM-DD date string
  includeArchived?: boolean;
  page?: number;
  limit?: number;
}

export interface CreateTransactionInput {
  type: string;
  status?: string;
  [key: string]: any;
}

export interface UpdateTransactionInput {
  type?: string;
  status?: string;
  comment?: string;
  amountIn?: string;
  currencyIn?: string;
  walletInId?: string;
  amountOut?: string;
  currencyOut?: string;
  walletOutId?: string;
  amount?: string;
  currency?: string;
  walletId?: string;
  exchangeRate?: string;
  oracleRate?: string;
  expectedAmount?: string;
  debtAmount?: string;
  debtAmountIn?: string;
  debtAmountOut?: string;
  expectedPaymentDate?: string;
  expectedPaymentDateIn?: string;
  expectedPaymentDateOut?: string;
  profitAmount?: string;
  profitCurrency?: string;
  category?: string;
  legalEntityId?: string;
  employeeId?: string;
  clientUuid?: string | null;
  clientId?: string;
  transactionDate?: string;
  createdAt?: string;
  version: number;
}

export interface DebtItem {
  id: string;
  type: string;
  status: string;
  createdAt: string;
  transactionDate?: string;
  amountIn: string | null;
  currencyIn: string | null;
  amountOut: string | null;
  currencyOut: string | null;
  expectedAmount: string | null;
  debtAmount: string | null;              // legacy
  debtAmountIn: string | null;
  debtAmountOut: string | null;
  originalDebtAmountIn: string | null;
  originalDebtAmountOut: string | null;
  clientId: string | null;
  clientName: string | null;
  comment: string | null;
  employeeId: string;
  employeeName: string;
}

export interface DebtDetail {
  transaction: DebtItem;
  payments: Array<{
    id: string;
    direction: 'in' | 'out';
    amount: string;
    currency: string;
    paymentDate: string;
    comment: string | null;
    recordedBy: string;
    recordedByName: string;
    walletName: string | null;
  }>;
  summary: {
    // OUT leg: we owe client
    originalOut: string | null;
    totalPaidOut: string;
    remainingOut: string | null;
    currencyOut: string | null;
    // IN leg: client owes us
    originalIn: string | null;
    totalPaidIn: string;
    remainingIn: string | null;
    currencyIn: string | null;
  };
}

export interface DebtFilters {
  page?: number;
  limit?: number;
}

export interface CreateDebtPayment {
  transactionId: string;
  direction: 'in' | 'out';
  amount: string;
  currency: string;
  walletId?: string;
  comment?: string;
  txHash?: string;
}

export interface ReportFilters {
  startDate?: string;
  endDate?: string;
  periodId?: string;
  type?: string;
  includeArchived?: boolean;
  includeAllEmployees?: boolean;
}

export interface CashflowReport {
  summary: any[];
  daily: any[];
}

export interface CashflowTransaction {
  id: string;
  type: string;
  status: 'open' | 'closed';
  createdAt: string;
  updatedAt?: string;
  transactionDate?: string;
  amountIn?: string;
  currencyIn?: string;
  walletInName?: string;
  amountOut?: string;
  currencyOut?: string;
  walletOutName?: string;
  amount?: string;
  currency?: string;
  walletName?: string;
  exchangeRate?: string;
  oracleRate?: string;
  debtAmount?: string;
  expectedAmount?: string;
  profitAmount?: string;
  profitCurrency?: string;
  clientId?: string;
  clientName?: string;
  clientUniqueId?: string;
  comment?: string;
  category?: string;
  employeeName: string;
}

export interface CashflowDetailedReport {
  data: CashflowTransaction[];
  meta: PaginationMeta;
}

export interface BalancesReport {
  byType: Record<string, any[]>;
  totals: CurrencyTotal[];
}

export interface DailySummaryReport {
  startDate: string;
  endDate: string;
  incoming: Array<{ currency: string; total: string; count: number }>;
  outgoing: Array<{ currency: string; total: string; count: number }>;
  profit: Array<{ currency: string; total: string }>;
  transactionCounts: Record<string, number>;
  openDebts: Array<{ currency: string; total: string; count: number }>;
}

export interface ProfitReport {
  byPair: any[];
  daily: any[];
  totals: CurrencyTotal[];
}

export interface DailyActivityReport {
  startDate: string;
  endDate: string;
  cashDeskName: string | null;
  summary: {
    totalBuy: { eur: string; usdt: string };
    totalSell: { eur: string; usdt: string };
    totalExpenses: { eur: string; usdt: string; rub?: string };
    totalDebt: { eur: string; usdt: string };
    totalDebtK?: { eur: string; usdt: string; rub: string };
    totalDebtD?: { eur: string; usdt: string; rub: string };
    counts?: {
      buy: number;
      sell: number;
      expense: number;
      debtK: number;
      debtD: number;
      debt: number;
    };
    profit: { eur: string; usdt: string };
    profitLoss: { eur: string | null; usdt: string };
    profitMargin: string;
    eurPurchaseRate: string | null;
    eurPurchaseRateSource: 'today' | 'previous' | null;
    eurPurchaseRateDate: string | null;
    expenseUsdt_inEur: string | null;
  };
  wallets: Array<{
    walletId: string;
    walletName: string;
    currency: string;
    openingBalance: string;
    closingBalance: string;
    difference: string;
  }>;
  transactions: {
    buy: Array<{ id: string; clientName: string; amountIn: string; currencyIn: string; rate: string; amountOut: string; currencyOut: string; profitEur: string | null }>;
    sell: Array<{ id: string; clientName: string; amountIn: string; currencyIn: string; rate: string; amountOut: string; currencyOut: string; profitEur: string | null }>;
    expense: Array<{
      id: string;
      name: string;
      eur: string;
      rate: string;
      usdt: string;
      rub?: string;
      groupType: 'salary' | 'taxes' | 'operational' | 'capital' | 'other' | null;
      employeeName: string | null;
      currency: string;
      transactionDate: string | null;
    }>;
    debt: Array<{
      id: string;
      clientName: string;
      eur: string;
      rate: string;
      usdt: string;
      debtK?: { eur: string; usdt: string; rub: string };
      debtD?: { eur: string; usdt: string; rub: string };
    }>;
  };
}

export interface RateItem {
  pair: string;
  rate: number;
  source: string;
  timestamp: string;
}

export interface RatesResponse {
  rates: RateItem[];
  supportedPairs?: string[];
  timestamp: string;
}

export interface CurrencyTotal {
  currency: string;
  amount: string;
  total?: string;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface AccountingPeriod {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  status: 'open' | 'closed';
  closedBy?: string;
  closedByName?: string;
  closedAt?: string;
}

export interface LogEntry {
  id: string;
  timestamp: string;
  level: string;
  message: string;
  context?: string;
  meta?: any;
}

export interface LogFilters {
  level?: string;
  context?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export interface CreateUserInput {
  username: string;
  password: string;
  fullName: string;
  email?: string;
  role: string;
  entityIds: string[];
}

export interface UpdateUserInput {
  fullName?: string;
  email?: string | null;
  role?: string;
  isActive?: boolean;
  entityIds?: string[];
}

export interface CreateEntityInput {
  code: string;
  name: string;
  country?: string;
  taxId?: string;
  defaultCurrency?: string;
}

export interface CreateWalletInput {
  name: string;
  type: string;
  currencyCode: string;
  address?: string;
  description?: string;
}

export interface CreatePeriodInput {
  name: string;
  startDate: string;
  endDate: string;
}

export interface Client {
  id: string;
  uniqueId: string;
  name: string;
  description?: string | null;
  isActive?: boolean;
  isEmployee?: boolean;
  createdAt: string;
  updatedAt?: string;
  transactionCount?: number;
  entity?: {
    id: string;
    code: string;
    name: string;
  };
}

export interface ClientFilters {
  search?: string;
  page?: number;
  limit?: number;
  isEmployee?: boolean;
}

export interface CreateClientInput {
  name: string;
  description?: string;
  isEmployee?: boolean;
}

export interface UpdateClientInput {
  name?: string;
  description?: string;
  isActive?: boolean;
  isEmployee?: boolean;
}

export interface WalletMovement {
  id: string;
  movementDate: string;
  createdAt: string;
  amount: string;
  currency: string;
  walletId: string;
  walletName: string;
  walletType: string;
  transactionId: string | null;
  txType: string | null;
  txStatus: string | null;
  description: string | null;
  clientName: string | null;
  isDebtPayment: boolean;
}

export interface CashDeskWallet {
  id: string;
  name: string;
  type: string;
  currencyCode: string;
  isActive: boolean;
}

export interface CashDeskUser {
  id: string;
  username: string;
  fullName: string;
  role: string;
}

export interface CashDesk {
  id: string;
  legalEntityId: string;
  name: string;
  description: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  wallets?: CashDeskWallet[];
  users?: CashDeskUser[];
}
